import { currentUserId, rememberedActiveBusiness } from "@/components/SavedAssets";

const KEY = 'appNotifications';

// The business the account was operating as when the notification was made:
// a Pro/Enterprise account's drawer follows the business switcher, so each
// notification remembers whose it is. Null for Express and guests.
const stampBusinessId = () => (rememberedActiveBusiness(currentUserId()) || {}).id || null;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// The bell drawer lives in this browser. When a user is signed in it is also
// mirrored to their account: every change is pushed (debounced), and the
// account's copy is merged in on load — so the list follows them to any device.
const userToken = () => { try { return localStorage.getItem('userToken') || ''; } catch { return ''; } };

export function getNotifications() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
}

// The local list belongs to ONE account. A different account signing in on
// this browser starts with a clean bell instead of inheriting (and syncing
// up!) the previous person's notifications. A guest list with no owner is
// kept: documents made signed-out merge into the account on first sign-in.
const OWNER_KEY = 'appNotificationsOwner';
export function claimNotifications(userId) {
  if (!userId) return;
  try {
    const owner = localStorage.getItem(OWNER_KEY) || '';
    if (owner && owner !== userId) localStorage.setItem(KEY, '[]');
    localStorage.setItem(OWNER_KEY, userId);
  } catch { /* storage blocked: nothing to claim */ }
}

let pushTimer = null;
function pushToAccount() {
  const token = userToken();
  if (!token) return;
  clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    fetch(`${BACKEND_URL}/api/user/notifications`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ notifications: getNotifications().slice(0, 200) }),
      keepalive: true,
    }).catch(() => {});
  }, 400);
}

function save(list) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* quota exceeded */ }
  pushToAccount();
}

// Signed in: merge the account's list into this device's (by id, newest first).
// Resolves true when something was merged so the caller can re-render.
export async function pullNotificationsFromAccount() {
  const token = userToken();
  if (!token) return false;
  try {
    const res = await fetch(`${BACKEND_URL}/api/user/notifications`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return false;
    const data = await res.json();
    const remote = Array.isArray(data.notifications) ? data.notifications : [];
    const local = getNotifications();
    const byId = new Map();
    [...local, ...remote].forEach((n) => { if (n && n.id && !byId.has(n.id)) byId.set(n.id, n); });
    const merged = [...byId.values()].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)).slice(0, 200);
    try { localStorage.setItem(KEY, JSON.stringify(merged)); } catch { /* quota exceeded */ }
    // Anything only this device had goes up to the account too
    if (local.some((n) => !remote.find((r) => r.id === n.id))) pushToAccount();
    return true;
  } catch {
    return false;
  }
}

// A readable, title-cased label for a document type, used as the {document} title token.
const DOC_LABELS = {
  paystub: "Pay Stub", "canadian-paystub": "Canadian Pay Stub", "uk-payslip": "UK Payslip", w2: "W-2", w9: "W-9",
  "1099-nec": "1099-NEC", "1099-misc": "1099-MISC", "bank-statement": "Accounting Mockup",
  "offer-letter": "Offer Letter", "legal-document": "Legal Document", resume: "Resume",
  "cease-and-desist": "Cease and Desist Letter", "power-of-attorney": "Power of Attorney",
  "commercial-lease": "Commercial Lease", "schedule-c": "Schedule C", "ai-resume": "Resume",
};
const docLabel = (t) =>
  DOC_LABELS[t] || String(t || "Document").replace(/-/g, " ").split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");

export function addGeneratingNotification({ id, type, fileName, fileType }) {
  const list = getNotifications();
  // titleKey/titleTokens let the admin re-title these in Site Settings; the
  // default template is "{fileName}", so the display is unchanged until edited.
  list.unshift({ id, type, fileName, fileType, businessId: stampBusinessId(), status: 'generating', timestamp: Date.now(), read: false, toastShown: false,
    titleKey: 'generated_document', titleTokens: { fileName: fileName || "", document: docLabel(type) } });
  save(list);
}

export function markNotificationReady(id) {
  const list = getNotifications();
  const n = list.find(n => n.id === id);
  if (n) { n.status = 'ready'; n.read = false; n.toastShown = false; }
  save(list);
}

// The archived copy's id — lets re-download and preview fetch the stored file
// on any device instead of depending on form data left in this browser.
export function setNotificationDocId(id, docId) {
  if (!docId) return;
  const list = getNotifications();
  const n = list.find(n => n.id === id);
  if (n) n.docId = docId;
  save(list);
}

// A document that is already finished: the drawer entry the payment flow
// builds in two steps, for the downloads that never pass through it (a
// subscription download goes straight from the form to the file). docId is the
// archived copy, which is what makes re-download work on another device.
export function addReadyNotification({ type, fileName, docId }) {
  const id = `notif_${Date.now()}_${type}`;
  addGeneratingNotification({ id, type, fileName, fileType: String(fileName || "").endsWith(".zip") ? "zip" : "pdf" });
  markNotificationReady(id);
  if (docId) setNotificationDocId(id, docId);
  return id;
}

export function markNotificationError(id) {
  const list = getNotifications();
  const n = list.find(n => n.id === id);
  if (n) n.status = 'error';
  save(list);
}

export function markToastShown(id) {
  const list = getNotifications();
  const n = list.find(n => n.id === id);
  if (n) n.toastShown = true;
  save(list);
}

export function markAllRead() {
  const list = getNotifications();
  list.forEach(n => { n.read = true; });
  save(list);
}

export function removeNotification(id) {
  save(getNotifications().filter(n => n.id !== id));
}

// The drawer's select mode: flip read state / delete for a set of ids
export function setNotificationsRead(ids, read = true) {
  const set = new Set(ids);
  const list = getNotifications();
  list.forEach(n => { if (set.has(n.id)) n.read = !!read; });
  save(list);
}

export function removeNotifications(ids) {
  const set = new Set(ids);
  save(getNotifications().filter(n => !set.has(n.id)));
}

export function clearAllNotifications() {
  save([]);
}

export function countUnread() {
  return getNotifications().filter(n => !n.read).length;
}

export function getPendingToasts() {
  // ready downloads, signature events, gifted downloads and referral notices all announce
  return getNotifications().filter(n => (n.status === 'ready' || n.signToken || n.titleKey === 'downloads_gifted' || n.type === 'referral') && !n.toastShown);
}

// Signature-request events (signed, declined, commented...) are not downloads:
// no "Ready to download" line, and clicking one opens the signing page.
const SIGN_TITLE_KEYS = new Set(['signed_final', 'signed_forwarded', 'declined', 'commented', 'signed_archived']);
export function isSignNotification(n) {
  return !!(n && (n.signToken || SIGN_TITLE_KEYS.has(n.titleKey)));
}
